/* eslint-disable no-console */
import React, { useMemo } from 'react';
import { useHistory, Link } from 'react-router-dom';
import store from 'store';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import Box from 'ui-box';
import { Button } from 'antd';
import InputField from '../form-helpers/InputField';
import { FormInputPassword, FormCheckbox } from '../form-helpers/FormField';
import LoadingSpinner from '../../common/LoadingSpinner';
import { LOCAL_STORAGE_AUTH_KEY } from '../../../config/constants';

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
}

const styles = {
  link: {
    fontWeight: "300",
    fontSize: "12px",
    lineHeight: "20px",
    textAlign: "center",
    color: "#15A9E1"
  }
}

function AccountDetailForm({ onSubmitBtnText }) {
  const { t } = useTranslation(['common', 'user-account']);
  const profileGuest = store.get(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE_GUEST);
  const history = useHistory();

  const FormSchema = useMemo(
    () =>
      Yup.object().shape({
        password: Yup.string()
          .required(t('form.error.required'))
          .matches(/[a-z]+/, t('form.error.oneLowerCase'))
          .matches(/[A-Z]+/, t('form.error.oneUpperCase'))
          .matches(/[@$!%*#?&]+/, t('form.error.oneSpecialCase'))
          .matches(/\d+/, t('form.error.oneNumber')),
        confirm_password: Yup.string()
          .required(t('form.error.required'))
          .oneOf([Yup.ref('password')], t('form.error.confirmPassword')),
        agree: Yup.string().required(t('form.error.required')),
      }),
    [t]
  );

  const initialValues = {
    email: '',
    password: '',
    confirm_password: '',
    agree: ''
  };

  const renderText = () => {
    return (
      <Trans i18nKey="sign-up:guest.termsSignup">
        I have read and understood the <Link to="/privacy" style={styles.link}>Terms of Service</Link> and <Link to="/privacy" style={styles.link}>Privacy Policy</Link> of of AlemHealth and give consent to process my information as mentioned.
      </Trans>
    )
  }

  return (
    <div style={{ width: '100%', textAlign: 'left' }}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={FormSchema}
        onSubmit={async (values) => {
          const dataForm = {}
          const { email, password } = values;
          dataForm.password = password
          dataForm.guid = store.get(LOCAL_STORAGE_AUTH_KEY.GUEST_ID)
          dataForm.email = email
          store.set(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE_SIGNUP, {...profileGuest, ...dataForm})
          store.set(LOCAL_STORAGE_AUTH_KEY.USER_PROFILE_GUEST, {...profileGuest, ...dataForm})
          history.push('/sign-up/form/personal-info');
        }}
      >
        {({ isSubmitting, isValid, setFieldValue }) => {
          return (
            <Form>
              <fieldset disabled={isSubmitting}>
                <FormInputPassword
                  type="password"
                  name="password"
                  style={{ width: '100%', padding: '3%' }}
                  formItemLayout={formItemLayout}
                  onChange={(e) => setFieldValue('password', e.target.value)}
                  label={t('common:form.label.password')}
                  placeholder={t('common:form.placeholder.password')}
                />
                <FormInputPassword
                  type="confirm_password"
                  name="confirm_password"
                  style={{ width: '100%', padding: '3%' }}
                  formItemLayout={formItemLayout}
                  onChange={(e) => setFieldValue('confirm_password', e.target.value)}
                  label={t('common:form.label.confirm_password')}
                  placeholder={t('common:form.placeholder.password')}
                />
                <InputField
                  type="email"
                  name="email"
                  label={`${t('common:form.label.emailAddress')} (${t('user-account:personalInformation.optional')})`}
                  placeholder={t('common:form.label.emailAddress')}
                />
                <FormCheckbox
                  type="checkbox"
                  name="agree"
                  formItemLayout={formItemLayout}
                  onChange={(e) => setFieldValue('agree', e.target.checked)}
                >
                  {renderText()}
                </FormCheckbox>
                <Box paddingTop={15}>
                  <Button
                    disabled={isSubmitting || !isValid}
                    loading={isSubmitting}
                    htmlType="submit"
                    type="primary"
                    size="large"
                    block
                  >
                    {isSubmitting ? <LoadingSpinner position="full" transparent={false} /> : onSubmitBtnText}
                  </Button>
                </Box>
              </fieldset>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

AccountDetailForm.defaultProps = {
  onSubmitBtnText: 'Sign Up',
};

const { string } = PropTypes;

AccountDetailForm.propTypes = {
  onSubmitBtnText: string,
};

export default AccountDetailForm;
